import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Spin } from 'antd'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import { Button } from '@crosscode/gravity-components'
import {
  fetchDocByTerminal,
  fetchDocsByTerminal,
  type OfdTerminalBinding,
} from '@/features/ofd-sbis/api'
import type { OfdDoc } from '@/features/ofd-sbis/types'
import { buildOfdReceiptModel, fmtMoney as fmtOfdMoney } from '@/features/ofd-sbis/ofdReceiptModel'
import { formatDateTime, shortDocType } from '@/features/ofd-sbis/utils'
import styles from './CashOfdFeed.module.css'

interface Props {
  fsNumber?: string | null
  serialNumber?: string | null
  enabled?: boolean
  pollMs?: number
}

const FLASH_MS = 2000

function todayYmd(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Moscow' })
}

function docKey(d: OfdDoc): string {
  return `${d.docType}-${d.fiscalDocumentNumber}-${d.fiscalSign ?? ''}`
}

function toDocYmd(ts?: string | number | null, formatted?: string): string {
  if (typeof ts === 'string' && /^\d{4}-\d{2}-\d{2}/.test(ts)) return ts.slice(0, 10)
  if (typeof ts === 'number') {
    const d = new Date(ts * 1000)
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }
  }
  if (typeof ts === 'string') {
    const d = new Date(ts)
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }
  }
  if (formatted && /^\d{2}\.\d{2}\.\d{4}/.test(formatted)) {
    const [dd, mm, yyyy] = formatted.slice(0, 10).split('.')
    return `${yyyy}-${mm}-${dd}`
  }
  return todayYmd()
}

function formatMoney(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '0,00'
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function OfdDarkReceipt({ payload }: { payload: Record<string, unknown> | null }) {
  const model = buildOfdReceiptModel(payload)
  if (!model) {
    return <div className={styles.paperEmpty}>Нет данных чека ОФД</div>
  }

  return (
    <>
      <section className={styles.paperSection}>
        <div className={styles.paperSectionTitle}>Реквизиты</div>
        <div className={styles.paperMeta}>
          {[
            model.user,
            model.retailPlace,
            model.dateLabel,
            model.operationLabel,
            model.paymentModeLabel,
            model.operator ? `Кассир ${model.operator}` : null,
            model.shiftNumber != null ? `Смена ${model.shiftNumber}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {model.address ? <div className={styles.paperMeta}>{model.address}</div> : null}
      </section>

      <section className={styles.paperSection}>
        <div className={styles.paperSectionTitle}>Товары</div>
        {model.items.length === 0 ? (
          <div className={styles.paperMeta}>Нет позиций</div>
        ) : (
          model.items.map((line) => (
            <div key={line.idx} className={styles.paperItem}>
              <div className={styles.paperItemHead}>
                <span className={styles.paperItemName}>
                  {line.idx + 1}. {line.name}
                </span>
                <span className={styles.paperItemSum}>{fmtOfdMoney(line.sum)} ₽</span>
              </div>
              <div className={styles.paperItemRow}>
                <span className={styles.paperMeta}>
                  {line.qtyLabel} {line.measure} × {fmtOfdMoney(line.price)} ₽
                  {line.meta ? ` · ${line.meta}` : ''}
                </span>
              </div>
            </div>
          ))
        )}
      </section>

      <div className={styles.paperTotals}>
        <div className={styles.paperTotalLine}>
          <span>Товаров</span>
          <span>{model.items.length}</span>
        </div>
        {model.ndsRows.map((row) => (
          <div key={row.label} className={styles.paperTotalLine}>
            <span>{row.label}</span>
            <span>{fmtOfdMoney(row.amount)} ₽</span>
          </div>
        ))}
        <div className={`${styles.paperTotalLine} ${styles.paperFinal}`}>
          <span>Итого</span>
          <span>{fmtOfdMoney(model.total)} ₽</span>
        </div>
      </div>

      {model.payments.length ? (
        <section className={styles.paperSection}>
          <div className={styles.paperSectionTitle}>Оплата</div>
          {model.payments.map((p) => (
            <div key={p.label} className={styles.paperPay}>
              <span>{p.label}</span>
              <span>{fmtOfdMoney(p.amount)} ₽</span>
            </div>
          ))}
        </section>
      ) : null}

      {model.fiscal.length || model.buyerAddress || model.senderAddress ? (
        <footer className={styles.paperFooter}>
          {model.fiscal.map((row) => (
            <div key={row.label} className={styles.paperFooterRow}>
              <span>{row.label}</span>
              <span>{row.value}</span>
            </div>
          ))}
          {model.buyerAddress ? <div>Покупатель: {model.buyerAddress}</div> : null}
          {model.senderAddress ? <div>Отправитель: {model.senderAddress}</div> : null}
        </footer>
      ) : null}
    </>
  )
}

export function CashOfdFeed({
  fsNumber,
  serialNumber,
  enabled = true,
  pollMs = 12000,
}: Props) {
  const [docs, setDocs] = useState<OfdDoc[]>([])
  const [binding, setBinding] = useState<OfdTerminalBinding | null>(null)
  const [stats, setStats] = useState<{
    receipt_count: number
    sum_closed: number
    avg_closed: number
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [payloads, setPayloads] = useState<Record<string, Record<string, unknown> | null>>({})
  const [payloadLoading, setPayloadLoading] = useState<Record<string, boolean>>({})
  const [flashIds, setFlashIds] = useState<Record<string, boolean>>({})

  const knownKeysRef = useRef<Set<string>>(new Set())
  const seededRef = useRef(false)
  const flashTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const listRef = useRef<HTMLUListElement>(null)
  const bindingRef = useRef<OfdTerminalBinding | null>(null)
  bindingRef.current = binding

  const canQuery = Boolean(
    (fsNumber && fsNumber !== 'Нет данных') || (serialNumber && serialNumber !== 'Нет данных'),
  )

  useEffect(() => {
    knownKeysRef.current = new Set()
    seededRef.current = false
    setDocs([])
    setBinding(null)
    setStats(null)
    setExpanded({})
    setPayloads({})
    setFlashIds({})
    setError(null)
    for (const t of flashTimersRef.current.values()) clearTimeout(t)
    flashTimersRef.current.clear()
  }, [fsNumber, serialNumber])

  const flashDoc = useCallback((key: string) => {
    setFlashIds((prev) => ({ ...prev, [key]: true }))
    const prevTimer = flashTimersRef.current.get(key)
    if (prevTimer) clearTimeout(prevTimer)
    const timer = setTimeout(() => {
      setFlashIds((prev) => {
        if (!prev[key]) return prev
        const next = { ...prev }
        delete next[key]
        return next
      })
      flashTimersRef.current.delete(key)
    }, FLASH_MS)
    flashTimersRef.current.set(key, timer)
  }, [])

  const load = useCallback(
    async (silent = false) => {
      if (!enabled || !canQuery) return
      if (!silent) setLoading(true)
      setError(null)
      try {
        const day = todayYmd()
        const data = await fetchDocsByTerminal({
          fsNumber,
          serialNumber,
          dateFrom: day,
          dateTo: day,
          limit: 200,
        })
        setBinding(data.binding)
        setStats(data.stats)
        const items = data.items || []

        if (!seededRef.current) {
          knownKeysRef.current = new Set(items.map(docKey))
          seededRef.current = true
          setDocs(items)
        } else {
          const fresh: string[] = []
          for (const d of items) {
            const k = docKey(d)
            if (!knownKeysRef.current.has(k)) {
              knownKeysRef.current.add(k)
              fresh.push(k)
            }
          }
          setDocs(items)
          if (fresh.length) {
            fresh.forEach(flashDoc)
            requestAnimationFrame(() => {
              const el = listRef.current?.querySelector(
                `[data-ofd-key="${fresh[0].replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`,
              )
              el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            })
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки чеков ОФД')
        if (!silent) {
          setDocs([])
          setStats(null)
        }
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [enabled, canQuery, fsNumber, serialNumber, flashDoc],
  )

  useEffect(() => {
    if (!enabled || !canQuery) return
    void load(false)
    const t = setInterval(() => void load(true), pollMs)
    return () => clearInterval(t)
  }, [enabled, canQuery, load, pollMs])

  const loadPayload = useCallback(async (doc: OfdDoc) => {
    const key = docKey(doc)
    const b = bindingRef.current
    if (!b || !doc.fiscalDocumentNumber) return
    const docDate = toDocYmd(doc.dateTime, doc.dateTimeFormatted)
    setPayloadLoading((prev) => ({ ...prev, [key]: true }))
    try {
      const data = await fetchDocByTerminal({
        inn: b.inn,
        regId: b.regId,
        storageId: b.storageId,
        docNum: doc.fiscalDocumentNumber,
        docDate,
      })
      setPayloads((prev) => ({ ...prev, [key]: data }))
    } catch {
      setPayloads((prev) => ({
        ...prev,
        [key]: doc.raw ? { ...(doc.raw as Record<string, unknown>) } : null,
      }))
    } finally {
      setPayloadLoading((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
  }, [])

  const toggle = (doc: OfdDoc) => {
    const key = docKey(doc)
    setExpanded((prev) => {
      const nextOpen = !prev[key]
      if (nextOpen && payloads[key] === undefined && !payloadLoading[key]) {
        void loadPayload(doc)
      }
      return { ...prev, [key]: nextOpen }
    })
  }

  const bindingLabel = useMemo(() => {
    if (!binding) return null
    const parts = [binding.model, binding.regId].filter(Boolean)
    return parts.join(' · ')
  }, [binding])

  if (!enabled) return null

  if (!canQuery) {
    return (
      <div className={styles.wrap}>
        <div className={styles.empty}>
          Нет номера ФН или S/N кассы — не к чему привязать чеки ОФД
        </div>
      </div>
    )
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.liveBar}>
        <span className={`${styles.liveDot} ${styles.liveDotOn}`} />
        <span>ОФД · онлайн</span>
        <span className={styles.liveHint}>каждые {Math.round(pollMs / 1000)} сек · сегодня</span>
        <Button size="small" onClick={() => void load(false)} loading={loading}>
          Обновить
        </Button>
      </div>

      {bindingLabel ? <div className={styles.bindingHint}>{bindingLabel}</div> : null}

      {stats ? (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Чеков</span>
            <span className={styles.kpiValue}>{stats.receipt_count}</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Сумма</span>
            <span className={styles.kpiValue}>{formatMoney(stats.sum_closed)} ₽</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Средний</span>
            <span className={styles.kpiValue}>{formatMoney(stats.avg_closed)} ₽</span>
          </div>
        </div>
      ) : null}

      {error ? <div className={styles.error}>{error}</div> : null}

      {loading && !docs.length ? (
        <div className={styles.empty}>
          <Spin size="small" />
        </div>
      ) : !docs.length && !error ? (
        <div className={styles.empty}>Пока нет чеков ОФД за сегодня</div>
      ) : docs.length ? (
        <ul className={styles.receiptList} ref={listRef}>
          {docs.map((d) => {
            const key = docKey(d)
            const open = !!expanded[key]
            const flashing = !!flashIds[key]
            return (
              <li
                key={key}
                data-ofd-key={key}
                className={[styles.receipt, flashing ? styles.receiptFlash : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <button type="button" className={styles.receiptHead} onClick={() => toggle(d)}>
                  <span className={styles.chevron}>{open ? <DownOutlined /> : <RightOutlined />}</span>
                  <span className={styles.receiptMain}>
                    <span className={styles.receiptTitle}>
                      {shortDocType(d.docType, d.docTypeName)} {d.fiscalDocumentNumber ?? '—'}
                      {flashing ? <span className={styles.newBadge}>новый</span> : null}
                    </span>
                    <span className={styles.receiptMeta}>
                      {formatDateTime(d.dateTime)}
                      {d.operationTypeName ? ` · ${d.operationTypeName}` : ''}
                      {d.totalSumRub != null ? ` · ${formatMoney(Number(d.totalSumRub))} ₽` : ''}
                      {d.shiftNumber != null ? ` · смена ${d.shiftNumber}` : ''}
                    </span>
                  </span>
                </button>

                {open ? (
                  <div className={styles.paper}>
                    {payloadLoading[key] ? (
                      <div className={styles.paperLoading}>
                        <Spin size="small" />
                      </div>
                    ) : (
                      <OfdDarkReceipt
                        payload={payloads[key] ?? (d.raw as Record<string, unknown>) ?? null}
                      />
                    )}
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
